import React, { useState } from 'react'
import validateEmail from '@/helpers/validateEmail'
import * as chatService from '@/services/chat.service'
import { defaultConversationStore } from '@/stores/conversation/conversationStore'
import { useTimezoneStore } from '@/stores/timezone/timezoneStore'
import { useUserStore } from '@/stores/user/userStore'

const EmailTranscriptWrapper = ({
  className = '',
  onCancel,
  onSuccess,
  token,
  conversationId,
  defaultEmail = '',
  handleMessagesOnChat,
  config,
  ...props
}) => {
  const currentConversation = defaultConversationStore((state) => state.currentConversation);
  const timezone = useTimezoneStore((state) => state.timezone);
  const auth = useUserStore((state) => state.auth);

  const [email, setEmail] = useState(defaultEmail);
  const [validationError, setValidationError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setEmail('');
    setValidationError('');
    onCancel && onCancel();
  };

  const handleGetChatTranscript = async () => {
    // Clear any previous validation errors
    setValidationError('');

    // Validate email input
    if (!email || !email.trim()) {
      setValidationError('Please enter an email address');
      return;
    }

    if (!validateEmail(email.trim())) {
      setValidationError('Please enter a valid email address');
      return;
    }

    const convId = conversationId || currentConversation?.id;
    if (!convId) {
      setValidationError('No conversation ID found');
      return;
    }

    setLoading(true);

    try {
      const response = await chatService.sendEmailTranscript({
        conversation_id: convId,
        token: token ?? auth?.access_token,
        email: email.trim(),
        timezone: timezone
      });

      if (response.status === 201) {
        // Add user message first (like "Ask a human" pattern)
        if (handleMessagesOnChat) {
          handleMessagesOnChat('add', {
            id: Date.now().toString(),
            content: `Send me the transcript of this chat to ${email.trim()}`,
            role: 'user',
            type: 'message',
            date_created: new Date().toISOString(),
          });

          // Then add assistant response
          setTimeout(() => {
            handleMessagesOnChat('add', {
              id: (Date.now() + 1).toString(),
              content: `I just sent an email transcript of this chat to ${email.trim()}. If you don't see it within 5 mins, check your spam folder.`,
              role: 'assistant',
              type: 'message',
              date_created: new Date().toISOString(),
            });
          }, 100);
        }

        // Close the modal immediately after sending
        onSuccess && onSuccess(email.trim());
        handleClose();
      } else {
        setValidationError('Failed to send email. Please try again.');
      }
    } catch (error) {
      console.log(error);
      setValidationError(error.response?.data?.message || 'Failed to send email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (validationError) {
      setValidationError('');
    }
  };

  return (
    <div 
      className={`flex flex-col p-v3-lg h-full font-inter-variable w-full z-[50] ${className}`} 
      style={{ backgroundColor: config?.surface_color || '#ffffff' }}
      {...props}
    >
      {/* Title */}
      <h2 className="text-v3-black-dark mb-v3-md text-v3-h4 font-bold">
       Where should I send the transcript?
      </h2>

      {/* Email Input */}
      <div className="w-full mb-v3-md">
        <label className="block text-sm font-medium text-v3-black-secondary mb-v3-xs">
          Email
        </label>
        <input
          type="email"
          value={email}
          onChange={handleEmailChange}
          placeholder="Enter your email address"
          className={`
            w-full
            px-v3-sm
            py-v3-xs
            rounded-v3-sm
            text-sm
            text-v3-black-primary
            placeholder-v3-black-placeholder
            text-v3-input
            border
            transition-all
            duration-200
            ease-in-out
            ${validationError
              ? 'border-v3-red-primary'
              : 'border-v3-black-placeholder'
            }
            ${loading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          style={{ backgroundColor: config?.surface_color || '#ffffff' }}
          disabled={loading}
        />

        {/* Error Message Container with Fixed Height and Transitions */}
        <div className="h-[16px] flex items-start transition-all duration-200 ease-in-out">
          {validationError && (
            <p className="text-v3-red-dark font-inter-variable text-xs font-medium animate-fade-in">
              {validationError}
            </p>
          )}
        </div>

        {/* Add spacing when error is present */}
        <div className={`transition-all duration-200 ease-in-out ${
          validationError ? 'mb-v3-xs' : 'mb-0'
        }`} />
      </div>

      {/* Buttons */}
      <div className="flex gap-v3-xs justify-end text-base ">
        <button
          onClick={handleClose}
          disabled={loading}
          className="w-[95px] h-[40px] px-v3-sm py-v3-xs rounded-v3-xl font-medium text-sm transition-colors duration-200 text-[var(--dt-color-brand-100)] bg-[var(--dt-color-brand-5)]"
        >
          Cancel
        </button>
        <button
          onClick={handleGetChatTranscript}
          disabled={loading}
          className={`w-[155px] h-[40px] text-white rounded-v3-xl font-medium text-sm transition-all duration-200 ease-in-out bg-[var(--dt-color-brand-100)] hover:bg-[var(--dt-color-brand-60)] ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {loading ? 'Sending...' : 'Send Transcript'}
        </button>
      </div>
    </div>
  )
}

export default EmailTranscriptWrapper